import { centsToInteger, integerToCents } from '@10xlabs/utils'
import { ApolloCache } from '@apollo/client'

import { INewPreferredSeatsAncillaryForm } from 'features/Ancillaries/components/NewPreferredSeatsAncillaryForm/types'
import {
  GTAncillary,
  GTAncillaryConditionsCitySegmentInput,
  GTAncillaryTypeInput,
  GTBookingV2Ticket,
  GTBookingV2TicketAncillary,
  GTBookingV2TicketAncillaryType,
  GTCreateAncillaryInput
} from 'gql/types.generated'
import { isEmpty, omit } from 'lodash'
import { isTicketCancelled } from 'resolvers/bookings'
import { getSegmentOriginsOrDestinations } from 'features/Segments/utils'

export const getAncillariesByTicket = (
  ticket: GTBookingV2Ticket,
  ancillaryTypes: GTBookingV2TicketAncillaryType[]
): GTBookingV2TicketAncillary[] | undefined => {
  const ancillaries = ticket.ancillaries ?? []
  return ancillaries.filter(({ type }) => ancillaryTypes.includes(type))
}

export const getTotalLuggageAmountByTicket = (ticket: GTBookingV2Ticket): number => {
  if (!isTicketCancelled(ticket)) {
    const lugageAncillaries = getAncillariesByTicket(ticket, [
      GTBookingV2TicketAncillaryType.Luggage,
      GTBookingV2TicketAncillaryType.CarryOn
    ])
    return lugageAncillaries?.reduce((acc, { total }) => acc + total, 0) ?? 0
  }

  return 0
}

export const getTotalPreferredSeatsAmountByTicket = (ticket: GTBookingV2Ticket): number => {
  if (!isTicketCancelled(ticket)) {
    const preferredSeatsAncillaries = getAncillariesByTicket(ticket, [GTBookingV2TicketAncillaryType.Seat])
    return preferredSeatsAncillaries?.reduce((acc, { total }) => acc + total, 0) ?? 0
  }

  return 0
}

export const getTotalLuggageAmountByTickets = (tickets: GTBookingV2Ticket[]): number => {
  let total = 0

  tickets.forEach((ticket) => {
    if (!isTicketCancelled(ticket)) {
      const totalByTicket = getTotalLuggageAmountByTicket(ticket)
      total += totalByTicket
    }
  })

  return total
}

export const getTotalPreferedSeatsAmountByTickets = (tickets: GTBookingV2Ticket[]): number => {
  let total = 0

  tickets.forEach((ticket) => {
    if (!isTicketCancelled(ticket)) {
      const totalByTicket = getTotalPreferredSeatsAmountByTicket(ticket)
      total += totalByTicket
    }
  })

  return total
}

export const mapPreferredSeatsAncillaryToMutation = (
  ancillaryForm: INewPreferredSeatsAncillaryForm
): Omit<GTCreateAncillaryInput, 'label' | 'name' | 'price'> => {
  const createRouteSegments = (
    origin: INewPreferredSeatsAncillaryForm['origin'],
    destination: INewPreferredSeatsAncillaryForm['destination']
  ): GTAncillaryConditionsCitySegmentInput => ({
    originID: origin.id,
    destinationID: destination.id,
    segmentIDs: ancillaryForm.allSegmentsAreSelected
      ? []
      : ancillaryForm.segments.filter((segment) => segment.cityOrigin.id === origin.id).map(({ id }) => id),
    routeServiceIDs: ancillaryForm.allServicesAreSelected
      ? []
      : ancillaryForm.routeServices
          .filter((routeService) => routeService.cityOrigin.id === origin.id)
          .map(({ id }) => id)
  })

  const citySegments = [createRouteSegments(ancillaryForm.origin, ancillaryForm.destination)]

  if (ancillaryForm.isReturn && ancillaryForm.origin.id !== ancillaryForm.destination.id) {
    citySegments.push(createRouteSegments(ancillaryForm.destination, ancillaryForm.origin))
  }

  return {
    companyID: ancillaryForm.company.id,
    conditions: {
      citySegments,
      salesChannelIDs: ancillaryForm.salesChannels.map(({ id }) => id),
      seatsPrice: {
        doubleDecker: ancillaryForm.doubleDeckPrices.map(({ price, ...rest }) => {
          return {
            ...rest,
            price: integerToCents(Number(price))
          }
        }),
        standard: ancillaryForm.singleDeckPrices.map(({ price, ...rest }) => {
          return {
            ...rest,
            price: integerToCents(Number(price))
          }
        })
      }
    },
    type: GTAncillaryTypeInput.Seat
  }
}

export const mapPreferredSeatsAncillaryToForm = (ancillary: GTAncillary): INewPreferredSeatsAncillaryForm => {
  const { company, conditions } = ancillary
  const doubleDeckerSeatRangePrices = conditions?.seatsPrice?.doubleDecker ?? []
  const singleDeckSeatRangePrices = conditions?.seatsPrice?.standard ?? []
  const citySegments = conditions?.citySegments ?? []
  const salesChannels = conditions?.salesChannels ?? []
  const origin = citySegments[0]?.origin
  const destination = citySegments[0]?.destination
  const doubleDeckPrices = doubleDeckerSeatRangePrices.map(({ price, ...rest }) => {
    return {
      ...omit(rest, ['__typename']),
      price: String(centsToInteger(price))
    }
  })

  const singleDeckPrices = singleDeckSeatRangePrices.map(({ price, ...rest }) => {
    return {
      ...omit(rest, ['__typename']),
      price: String(centsToInteger(price))
    }
  })

  const segments = citySegments.flatMap(({ segments, origin, destination }) =>
    segments.map((segment) => ({
      ...segment,
      cityOrigin: origin,
      cityDestination: destination
    }))
  )
  const routeServices = citySegments.flatMap(({ routeServices, origin, destination }) =>
    (routeServices ?? []).map((routeService) => ({
      ...routeService,
      cityOrigin: origin,
      cityDestination: destination
    }))
  )
  return {
    allSegmentsAreSelected:
      !isEmpty(citySegments) && citySegments.every((citySegment) => citySegment?.segments.length === 0),
    allServicesAreSelected:
      !isEmpty(citySegments) && citySegments.every((citySegment) => citySegment?.routeServices?.length === 0),
    countryCode: conditions?.salesChannels?.[0]?.country?.code ?? '',
    isReturn: citySegments?.length === 2,
    company: company ?? {
      id: '',
      name: ''
    },
    doubleDeckPrices: doubleDeckPrices ?? [],
    routeServices: routeServices ?? [],
    salesChannels: salesChannels ?? [],
    origin: origin ?? {
      id: '',
      name: ''
    },
    destination: destination ?? {
      id: '',
      name: ''
    },
    segments: segments ?? [],
    singleDeckPrices: singleDeckPrices ?? [],
    segmentsOrigins: getSegmentOriginsOrDestinations(segments, 'origin'),
    segmentsDestinations: getSegmentOriginsOrDestinations(segments, 'destination')
  }
}

export const toggleActivationLocalAncillary = (
  cache: ApolloCache<unknown>,
  ancillaryId: string,
  value: boolean
): void => {
  cache.modify({
    id: cache.identify({
      __typename: 'Ancillary',
      id: ancillaryId
    }),
    fields: {
      isEnabled: () => value
    }
  })
}
